import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, X } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULES = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /[0-9]/.test(p) },
];

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [pwd, setPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const ruleStatus = useMemo(() => RULES.map(r => ({ ...r, ok: r.test(pwd) })), [pwd]);
  const allOk = ruleStatus.every(r => r.ok);
  const matches = pwd.length > 0 && pwd === confirmPwd;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { setError('Missing reset token.'); return; }
    if (!allOk) { setError('Password does not meet requirements.'); return; }
    if (!matches) { setError('Passwords do not match.'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/auth?mode=signin'), 2200);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={56} />
          <h1 className="mt-4 text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Law Agency 360</h1>
          <p className="text-sm text-slate-400 mt-1">Choose a new password</p>
        </div>
        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          {done ? (
            <div className="text-center" data-testid="reset-password-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Password updated</h2>
              <p className="text-sm text-slate-400">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label className="text-sm text-slate-300 mb-2 block">New password</label>
                <input
                  type="password"
                  required
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="w-full bg-slate-800/60 border border-white/10 text-white rounded-xl px-4 py-3 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition"
                  data-testid="reset-password-new"
                />
              </div>
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Confirm password</label>
                <input
                  type="password"
                  required
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  className="w-full bg-slate-800/60 border border-white/10 text-white rounded-xl px-4 py-3 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition"
                  data-testid="reset-password-confirm"
                />
              </div>
              <ul className="space-y-1 text-xs">
                {ruleStatus.map(r => (
                  <li key={r.id} className="flex items-center gap-2">
                    {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <X className="w-3.5 h-3.5 text-slate-500" />}
                    <span className={r.ok ? 'text-slate-200' : 'text-slate-500'}>{r.label}</span>
                  </li>
                ))}
                <li className="flex items-center gap-2">
                  {matches ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <X className="w-3.5 h-3.5 text-slate-500" />}
                  <span className={matches ? 'text-slate-200' : 'text-slate-500'}>Passwords match</span>
                </li>
              </ul>
              {error && <p className="text-sm text-red-400" data-testid="reset-password-error">{error}</p>}
              <button
                type="submit"
                disabled={loading || !allOk || !matches}
                className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="reset-password-submit"
              >
                {loading ? 'Updating…' : 'Update Password'}
              </button>
              <Link to="/auth?mode=signin" className="block text-center text-sm text-slate-400 hover:text-violet-300">
                Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
